import React, { useEffect, useRef, useState } from "react";
import { postApi } from "../../../helpers/auth_helpers";
import { fileTypeFor, MAX_MS, pickAudioMime, seconds } from "./voiceFormat";
import { useKT } from "../i18n/kt";

const base = process.env.REACT_APP_SERVERURL || "";

/** Upload a recorded note to S3 through a short-lived address; returns its permanent https address. */
const uploadVoiceNote = async (blob: Blob, mime: string): Promise<string> => {
  const res: any = await postApi(`${base}/karkhana/voice-notes/upload-url`, { fileType: fileTypeFor(mime) });
  const url: string = res?.data;
  if (!res?.success || !url) throw new Error(res?.message || "Could not get an upload address");
  const put = await fetch(url, { method: "PUT", headers: { "Content-Type": mime.split(";")[0] }, body: blob });
  if (!put.ok) throw new Error("The voice note didn't upload");
  return url.split("?")[0];
};

/**
 * Record a short voice note explaining a late entry (K17). Tap to record, tap to stop, listen,
 * then it uploads. Needs signal; without it the entry can still be saved without a note.
 */
const VoiceNote = ({ value, onChange }: { value: string | null; onChange: (url: string | null) => void }) => {
  const { t } = useKT();
  const [state, setState] = useState<"idle" | "recording" | "uploading" | "error">("idle");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState("");
  const rec = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const started = useRef(0);
  const tick = useRef<number | undefined>(undefined);

  useEffect(() => () => {
    window.clearInterval(tick.current);
    rec.current?.stream.getTracks().forEach((t) => t.stop());
  }, []);

  const start = async () => {
    setError("");
    const mime = typeof MediaRecorder !== "undefined" ? pickAudioMime((m) => MediaRecorder.isTypeSupported(m)) : null;
    if (!mime || !navigator.mediaDevices?.getUserMedia) {
      setError("This phone's browser can't record audio here.");
      setState("error");
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setError("Microphone is blocked. Allow it for this site and try again.");
      setState("error");
      return;
    }
    const r = new MediaRecorder(stream, { mimeType: mime });
    chunks.current = [];
    r.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
    r.onstop = async () => {
      window.clearInterval(tick.current);
      stream.getTracks().forEach((t) => t.stop());
      const blob = new Blob(chunks.current, { type: mime.split(";")[0] });
      setState("uploading");
      try {
        onChange(await uploadVoiceNote(blob, mime));
        setState("idle");
      } catch (e: any) {
        setError(t(e?.message || "The voice note didn't upload. Check the signal and record again."));
        setState("error");
      }
    };
    rec.current = r;
    started.current = Date.now();
    setElapsed(0);
    r.start();
    setState("recording");
    tick.current = window.setInterval(() => {
      const ms = Date.now() - started.current;
      setElapsed(ms);
      if (ms >= MAX_MS && r.state === "recording") r.stop();
    }, 250);
  };

  const stop = () => {
    if (rec.current?.state === "recording") rec.current.stop();
  };

  return (
    <div className="kr-voice">
      <span className="kr-label" style={{ marginTop: 0 }}>
        {t("Voice note for the admin (optional)")}
      </span>
      {value ? (
        <div className="kr-voice__row">
          <audio controls src={value} preload="none" />
          <button type="button" className="kr-choice" onClick={() => onChange(null)}>
            {t("Remove")}
          </button>
        </div>
      ) : state === "recording" ? (
        <button type="button" className="kr-choice is-on kr-voice__rec" onClick={stop}>
          ■ {t("Stop")} · {seconds(elapsed)}
        </button>
      ) : (
        <button type="button" className="kr-choice" disabled={state === "uploading"} onClick={start}>
          {state === "uploading" ? t("Saving the note…") : t("🎙 Record why it's late")}
        </button>
      )}
      {error ? <div className="kw-muted mt-1">{t(error)}</div> : null}
    </div>
  );
};

export default VoiceNote;
